import type { LibraryShelf } from "@/cloud/types/types";
import type { ShelfBoardView, ShelfLayout } from "@/shelf/types/types";

import { boardPointFromViewport } from "@/shelf/utils/board-projection";
import { resolveShelfLayout } from "@/shelf/utils/shelf-layout";

/**
 * How far past the window a rack is still drawn, so one panned into view is
 * already there rather than appearing as the edge reaches it.
 */
const OVERSCAN_MILLIMETERS = 200;

/**
 * The racks worth drawing, and their layouts.
 *
 * A board is unbounded and a rack is a few hundred elements, so the racks
 * nobody is looking at are the entire cost of a large collection — and this is
 * why the board is drawn in the DOM at all rather than painted into a canvas:
 * what a canvas would buy is drawing a thousand racks cheaply, and not drawing
 * them is cheaper still. Everything on screen stays a real element with a real
 * accessible name.
 *
 * Answered by arithmetic, never by measuring, so it holds at any zoom and for
 * racks that have never been laid out.
 */
export const selectVisibleShelves = ({
  shelves,
  view,
  viewportWidthPixels,
  viewportHeightPixels,
}: {
  shelves: readonly LibraryShelf[];
  view: ShelfBoardView;
  viewportWidthPixels: number;
  viewportHeightPixels: number;
}): ShelfLayout[] => {
  const topLeft = boardPointFromViewport({ view, xPixels: 0, yPixels: 0 });
  const bottomRight = boardPointFromViewport({
    view,
    xPixels: viewportWidthPixels,
    yPixels: viewportHeightPixels,
  });
  const left = topLeft.xMillimeters - OVERSCAN_MILLIMETERS;
  const top = topLeft.yMillimeters - OVERSCAN_MILLIMETERS;
  const right = bottomRight.xMillimeters + OVERSCAN_MILLIMETERS;
  const bottom = bottomRight.yMillimeters + OVERSCAN_MILLIMETERS;

  return shelves
    .map(resolveShelfLayout)
    .filter(
      ({ boundingBox }) =>
        boundingBox.xMillimeters <= right &&
        boundingBox.xMillimeters + boundingBox.widthMillimeters >= left &&
        boundingBox.yMillimeters <= bottom &&
        boundingBox.yMillimeters + boundingBox.heightMillimeters >= top,
    );
};
